import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Mail, Lock, ArrowRight, Shield, HeartPulse,
  CheckCircle2, ChevronLeft, Send, KeyRound, ShieldCheck,
} from 'lucide-react';
import { TextInput, Alert } from '../components/UI';
import { API_URL, API_KEY } from '../config';

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .pw-root {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Sans', system-ui, sans-serif;
    background: #f0f4f8;
    padding: 40px 20px;
    position: relative;
    overflow: hidden;
  }

  .pw-root::before {
    content: '';
    position: fixed; inset: 0;
    background-image: radial-gradient(circle, rgba(0,80,140,0.055) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none;
  }

  .pw-blob1 {
    position: fixed; top: -160px; left: -160px;
    width: 480px; height: 480px; border-radius: 50%;
    background: radial-gradient(circle, rgba(0,130,200,0.09) 0%, transparent 70%);
    pointer-events: none;
  }
  .pw-blob2 {
    position: fixed; bottom: -120px; right: -120px;
    width: 400px; height: 400px; border-radius: 50%;
    background: radial-gradient(circle, rgba(0,160,200,0.07) 0%, transparent 70%);
    pointer-events: none;
  }

  .pw-card {
    position: relative; z-index: 1;
    width: 100%; max-width: 430px;
    background: #ffffff;
    border-radius: 20px;
    padding: 44px 48px 40px;
    box-shadow: 0 8px 40px rgba(0,60,120,0.10), 0 1px 4px rgba(0,60,120,0.06);
    border: 1px solid rgba(0,100,160,0.08);
  }

  .pw-logo {
    display: block;
    width: 220px; height: 44px;
    object-fit: contain;
    margin: 0 auto 16px;
  }
  .pw-logo-rule {
    width: 40px; height: 2px;
    background: linear-gradient(90deg, rgba(0,130,200,0.5), transparent);
    border-radius: 2px;
    margin: 0 auto 24px;
  }

  .pw-chip {
    display: inline-flex; align-items: center;
    padding: 4px 12px;
    background: rgba(0,110,170,0.08);
    border: 1px solid rgba(0,110,170,0.16);
    border-radius: 100px;
    font-size: 10px; font-weight: 600;
    letter-spacing: 1.6px; text-transform: uppercase;
    color: #006090; margin-bottom: 12px;
  }

  .pw-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 30px; font-weight: 700;
    color: #0d2a42; letter-spacing: -0.3px;
    line-height: 1.13; margin-bottom: 6px;
  }

  .pw-sub {
    font-size: 13.5px; color: #7a96b0;
    line-height: 1.65; font-weight: 300; margin-bottom: 24px;
  }

  .pw-fields { display: flex; flex-direction: column; gap: 14px; }

  .pw-cta {
    width: 100%; height: 50px; border-radius: 12px;
    background: linear-gradient(128deg, #005a8e 0%, #0090c0 100%);
    border: none; color: #fff;
    font-size: 15px; font-weight: 600;
    font-family: 'DM Sans', system-ui, sans-serif;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    box-shadow: 0 4px 18px rgba(0,90,142,0.22);
    transition: transform 0.18s ease, box-shadow 0.18s ease;
    position: relative; overflow: hidden;
    margin-top: 22px;
  }
  .pw-cta::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 50%;
    background: linear-gradient(180deg, rgba(255,255,255,0.12) 0%, transparent 100%);
    pointer-events: none;
  }
  .pw-cta:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 26px rgba(0,90,142,0.30); }
  .pw-cta:active:not(:disabled) { transform: translateY(0); }
  .pw-cta:disabled { opacity: 0.6; cursor: not-allowed; }

  .pw-or { display: flex; align-items: center; gap: 14px; margin: 20px 0; }
  .pw-or-line { flex: 1; height: 1px; background: #dde6ef; }
  .pw-or-txt  { font-size: 12px; color: #9ab0c4; white-space: nowrap; }

  .pw-ghost {
    width: 100%; height: 48px; border-radius: 12px;
    border: 1.5px solid #c4d2e0; background: #f7fafd;
    color: #0d2a42; font-size: 14px; font-weight: 500;
    font-family: 'DM Sans', system-ui, sans-serif;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 7px;
    transition: background 0.18s, border-color 0.18s;
  }
  .pw-ghost:hover { background: #e6f0f8; border-color: rgba(0,130,190,0.3); }

  .pw-success {
    display: flex; gap: 14px; align-items: flex-start;
    background: rgba(5,150,105,0.07);
    border: 1px solid rgba(5,150,105,0.22);
    border-left: 3px solid #059669;
    border-radius: 12px; padding: 16px 18px;
    margin-bottom: 20px;
  }
  .pw-success-ico {
    width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0;
    background: rgba(5,150,105,0.12);
    display: flex; align-items: center; justify-content: center;
  }
  .pw-success-title { font-size: 14px; font-weight: 700; color: #065f46; margin-bottom: 4px; }
  .pw-success-sub   { font-size: 12.5px; color: #047857; line-height: 1.6; }

  .pw-token {
    display: flex; align-items: center; gap: 9px;
    background: rgba(0,110,170,0.07);
    border: 1px solid rgba(0,110,170,0.15);
    border-radius: 10px; padding: 11px 14px;
    margin-bottom: 20px;
  }
  .pw-token span { font-size: 12.5px; color: #006090; font-weight: 500; }

  .pw-trust {
    display: flex; align-items: center; justify-content: center;
    gap: 18px; margin-top: 20px; padding-top: 16px;
    border-top: 1px solid #dde6ef;
  }
  .pw-badge { display: flex; align-items: center; gap: 5px; font-size: 10.5px; color: #96aec4; }

  .pw-footer-note {
    text-align: center; margin-top: 14px;
    font-size: 11px; color: #a0b4c8; line-height: 1.7;
  }

  @keyframes pw-up { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
  .a-u0 { animation: pw-up 0.6s cubic-bezier(0.16,1,0.3,1) 0.04s both; }
  .a-u1 { animation: pw-up 0.6s cubic-bezier(0.16,1,0.3,1) 0.12s both; }
  .a-u2 { animation: pw-up 0.6s cubic-bezier(0.16,1,0.3,1) 0.20s both; }
  .a-u3 { animation: pw-up 0.6s cubic-bezier(0.16,1,0.3,1) 0.28s both; }
  .a-u4 { animation: pw-up 0.6s cubic-bezier(0.16,1,0.3,1) 0.36s both; }

  @media (max-width: 520px) {
    .pw-card  { padding: 32px 22px 28px; }
    .pw-title { font-size: 26px; }
    .pw-logo  { width: 180px; height: 36px; }
  }
`;

function TrustFooter() {
  return (
    <>
      <div className="pw-trust a-u4">
        {[['Encrypted', Shield], ['HIPAA Aligned', CheckCircle2], ['Wellness Only', HeartPulse]].map(([lbl, Icon]) => (
          <div key={lbl} className="pw-badge">
            <Icon size={12} color="#96aec4" /><span>{lbl}</span>
          </div>
        ))}
      </div>
      <p className="pw-footer-note">
        For authorized wellness professionals only.<br />
        Unauthorized access is strictly prohibited.
      </p>
    </>
  );
}

/* ── FORGOT PASSWORD ── */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Please enter a valid email address.'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) setSent(true);
      else setError(data.message || 'Something went wrong.');
    } catch { setError('Cannot connect to server.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="pw-root">
        <div className="pw-blob1" />
        <div className="pw-blob2" />
        <div className="pw-card">

          <div className="a-u0">
            <img src="/logo.png" alt="Cytometrix" className="pw-logo" />
            <div className="pw-logo-rule" />
          </div>

          <div className="a-u0">
            <div className="pw-chip">Account Recovery</div>
            <h2 className="pw-title">Forgot password?</h2>
            <p className="pw-sub">Enter your registered email and we'll send you a secure reset link.</p>
          </div>

          {sent ? (
            <>
              <div className="pw-success a-u1">
                <div className="pw-success-ico">
                  <Send size={19} color="#059669" strokeWidth={2} />
                </div>
                <div>
                  <p className="pw-success-title">Check your inbox</p>
                  <p className="pw-success-sub">
                    A reset link was sent to <strong>{email}</strong>. It expires in 10 minutes.
                  </p>
                </div>
              </div>
              <div className="a-u2">
                <button className="pw-ghost" onClick={() => navigate('/login')}>
                  <ChevronLeft size={15} strokeWidth={2.5} /> Return to Login
                </button>
              </div>
            </>
          ) : (
            <>
              {error && <div style={{ marginBottom: 16 }} className="a-u1"><Alert type="error">{error}</Alert></div>}
              <form onSubmit={handleSubmit}>
                <div className="pw-fields a-u1">
                  <TextInput
                    label="Email Address" icon={Mail} type="email" required
                    value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="your@hospital.ca"
                    hint="Must match the email registered with your account."
                  />
                </div>
                <button type="submit" className="pw-cta a-u2" disabled={loading}>
                  {loading ? 'Sending…' : <><span>Send Reset Link</span><ArrowRight size={16} strokeWidth={2.5} /></>}
                </button>
              </form>
              <div className="pw-or a-u3">
                <div className="pw-or-line" />
                <span className="pw-or-txt">Remember your password?</span>
                <div className="pw-or-line" />
              </div>
              <div className="a-u4">
                <button className="pw-ghost" onClick={() => navigate('/login')}>
                  <ChevronLeft size={15} strokeWidth={2.5} /> Back to Login
                </button>
              </div>
            </>
          )}

          <TrustFooter />
        </div>
      </div>
    </>
  );
}

/* ── RESET PASSWORD ── */
export function ResetPasswordPage() {
  const { token }  = useParams();
  const navigate   = useNavigate();
  const [form, setForm]         = useState({ password: '', confirm: '' });
  const [errors, setErrors]     = useState({});
  const [loading, setLoading]   = useState(false);
  const [apiError, setApiError] = useState('');

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.password || form.password.length < 8) errs.password = 'Password must be at least 8 characters.';
    if (form.password !== form.confirm) errs.confirm = 'Passwords do not match.';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true); setApiError('');
    try {
      const res = await fetch(`${API_URL}/reset-password/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify({ password: form.password }),
      });
      const data = await res.json();
      if (res.ok) navigate('/login', { state: { message: 'Password reset successfully. Please sign in.' } });
      else setApiError(data.message || 'Failed to reset password.');
    } catch { setApiError('Cannot connect to server.'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="pw-root">
        <div className="pw-blob1" />
        <div className="pw-blob2" />
        <div className="pw-card">

          <div className="a-u0">
            <img src="/logo.png" alt="Cytometrix" className="pw-logo" />
            <div className="pw-logo-rule" />
          </div>

          <div className="a-u0">
            <div className="pw-chip">Set New Password</div>
            <h2 className="pw-title">Reset your password</h2>
            <p className="pw-sub">Create a new secure password for your Cytometrix account.</p>
          </div>

          <div className="pw-token a-u1">
            <ShieldCheck size={14} color="#006090" strokeWidth={2.2} />
            <span>Secure link verified — proceed to set your new password.</span>
          </div>

          {apiError && <div style={{ marginBottom: 16 }} className="a-u1"><Alert type="error">{apiError}</Alert></div>}

          <form onSubmit={handleSubmit}>
            <div className="pw-fields a-u2">
              <TextInput
                label="New Password" icon={Lock} type="password" required
                value={form.password} onChange={e => set('password', e.target.value)}
                placeholder="Minimum 8 characters"
                error={errors.password}
                hint={!errors.password ? 'Use a mix of letters, numbers and symbols.' : undefined}
              />
              <TextInput
                label="Confirm New Password" icon={KeyRound} type="password" required
                value={form.confirm} onChange={e => set('confirm', e.target.value)}
                placeholder="Repeat your new password"
                error={errors.confirm}
              />
            </div>
            <button type="submit" className="pw-cta a-u3" disabled={loading}>
              {loading ? 'Resetting…' : <><span>Reset Password</span><ArrowRight size={16} strokeWidth={2.5} /></>}
            </button>
          </form>

          <div className="pw-or a-u4">
            <div className="pw-or-line" />
            <span className="pw-or-txt">Remember your password?</span>
            <div className="pw-or-line" />
          </div>

          <div className="a-u4">
            <button className="pw-ghost" onClick={() => navigate('/login')}>
              <ChevronLeft size={15} strokeWidth={2.5} /> Return to Login
            </button>
          </div>

          <TrustFooter />
        </div>
      </div>
    </>
  );
}